import { CandidateFormQuestion } from '@percent/partner-dashboard/services/grants/grantsService.types'

/**
 * A `money` field is authored as two questions sharing a key prefix: `<prefix>.amount` and
 * `<prefix>.currency`. We group by that prefix (supporting multiple pairs per page) and render the pair as a
 * single field, while keeping both underlying questions registered by their own id for validation/submission.
 */
export type MoneyGroup = { amount?: CandidateFormQuestion; currency?: CandidateFormQuestion }

const MONEY_SUFFIX = /\.(amount|currency)$/

export const moneyKeyPrefix = (key: string): string | undefined => {
  const match = MONEY_SUFFIX.exec(key)

  return match ? key.slice(0, match.index) : undefined
}

export const buildMoneyGroups = (questions: CandidateFormQuestion[]): Map<string, MoneyGroup> => {
  const groups = new Map<string, MoneyGroup>()

  for (const question of questions) {
    const prefix = question.type === 'money' ? moneyKeyPrefix(question.key) : undefined

    if (prefix !== undefined) {
      const group = groups.get(prefix) ?? {}

      if (question.key.endsWith('.amount')) {
        group.amount = question
      } else {
        group.currency = question
      }

      groups.set(prefix, group)
    }
  }

  return groups
}

export type MoneyRenderDecision =
  // A `.currency` half whose `.amount` sibling exists: rendered inside the amount's field, so skip it here.
  { render: false } | { render: true; group: MoneyGroup; currencyOnly: boolean }

/**
 * Decides how a `money` question participates in rendering. Returns `undefined` for non-money questions so the
 * caller falls back to the normal per-question renderer.
 */
export const moneyRenderDecision = (
  question: CandidateFormQuestion,
  groups: Map<string, MoneyGroup>
): MoneyRenderDecision | undefined => {
  if (question.type !== 'money') return undefined

  const prefix = moneyKeyPrefix(question.key)

  if (prefix === undefined) return { render: true, group: { amount: question }, currencyOnly: false }

  const group = groups.get(prefix) ?? {}

  if (question.key.endsWith('.amount')) {
    return { render: true, group, currencyOnly: false }
  }

  // `.currency` half — only render it standalone when there is no sibling amount.
  return group.amount ? { render: false } : { render: true, group, currencyOnly: true }
}
